<template>
  <sui-segment class="log" :class="[entity.target, {multicontainer: isMulticontainerService}]" basic>
    <sui-form class="filter" v-show="isFilterVisible && hasSearch" @submit.prevent>
      <sui-input ref="text" class="filter-input" type="Search" v-model="filter.text" icon="search" icon-position="left" :loading="lazyLoading" @input="searchText($event)" @blur="hideFilter()" label="" />
    </sui-form>
    <div ref="list" class="list scrolling" :style="{height: `${height}px`}" @scroll="onScroll($event)">
      <sui-button class="status" size="tiny" :loading="lazyLoading" basic fluid v-show="hasSearch" @click="loadPrevPage()">
        {{isBeginning ? 'The beginning.' : 'Load more'}}
      </sui-button>
      <div class="no-logs" v-if="!logs.length">
        No records yet.
      </div>
      <div class="days" v-if="logs.length">
        <div class="day" v-for="(dayLogs, dayTitle) in logsByDay" :key="dayTitle">
          <div class="date">{{dayTitle}}</div>
          <LogMessage :message="message" v-for="message in dayLogs" :key="message.time" @selectMessage="searchMessage" />
        </div>
      </div>
      <sui-button class="status" size="tiny" :loading="lazyLoading" basic fluid v-show="hasSearch" @click="loadNextPage()">
        {{isEnd ? 'The end.' + (!logProvider && live ? ' Awaiting new records...' : '') : 'Load more'}}
      </sui-button>
    </div>
    <sui-message class="log-provider" warning attached="bottom" v-if="logProvider">
      All new log messages of the container are redirected to the selected log provider:
      <a class="ui label" @click="openLogProviderSelector()"><Logo inline :providerType="logProvider.type" />{{logProvider.name}}</a>
    </sui-message>
    <sui-button class="filter-button" icon="search" v-show="!isFilterVisible && hasSearch" @click="showFilter()" />
  </sui-segment>
</template>

<script>
  import moment from 'moment'
  import {mapGetters} from 'vuex'
  import debounce from 'lodash/debounce'
  import groupBy from 'lodash/groupBy'
  import api from '@/api'
  import logger from '@/logger'
  import utils from '@/utils'
  import LogMessage from '@/components/LogMessage'

  export default {
    name: 'EntityLog',
    props: {
      entity: {
        type: Object,
        required: true,
      },
      height: {
        type: Number,
        required: false,
        default: 600,
      },
    },
    components: {
      LogMessage,
    },
    data() {
      const hasSearch = Boolean(this.entity.target)
      return {
        count: 50,
        fromBottom: 0,
        fromTop: 0,
        hasSearch,
        isBeginning: false,
        isEnd: false,
        isFilterVisible: false,
        lazyLoad: hasSearch,
        lazyLoadOffset: 50,
        lazyLoading: false,
        live: false,
        logs: [],
        filter: {
          direction: -1,
          include: true,
          text: '',
          type: '',
          time: '',
          limit: 25
        },
      }
    },
    computed: {
      ...mapGetters(['providersById']),
      logsByDay() {
        return groupBy(this.logs, log => log.dayTitle)
      },
      firstMessage() {
        return this.logs[0] || {time: ''}
      },
      lastMessage() {
        return this.logs[this.logs.length - 1] || {time: ''}
      },
      logProvider() {
        return this.entity.target === 'container' && this.providersById[this.entity._service.logProvider]
      },
      isMulticontainerService() {
        return this.entity.target === 'service' && this.entity._containers.length > 1
      },
    },
    sockets: {
      reconnect() {
        this.subscribe()
      },
      logs(data) {
        const sameId = this.entity.id === data.id
        const sameTarget = this.entity.target === data.target
        if (sameId && sameTarget) {
          this.pushMessage(formatMessage(data.data))
        }
      },
    },
    watch: {
      entity(newEntity, oldEntity) {
        if (newEntity.id !== oldEntity.id) {
          this.unsubscribe(oldEntity.target)
          this.isEnd = false
          this.init()
        }
      },
      isEnd(newValue) {
        if (newValue) {
          this.subscribe()
        } else {
          this.unsubscribe()
        }
      },
      'filter.text'(newValue) {
        if (newValue) {
          this.unsubscribe()
        } else {
          this.subscribe()
        }
      },
    },
    methods: {
      init() {
        this.search({limit: this.count}).then(newLogs => {
          this.logs = newLogs
          this.isEnd = true
          if (this.logs.length < this.count) {
            this.isBeginning = true
          }
          this.$nextTick(() => {
            if (this.$refs.list) {
              this.$refs.list.scrollTop = this.$refs.list.scrollHeight
            }
          })
        })
      },
      subscribe() {
        if (!this.live && !this.filter.text && this.isEnd) {
          const {id, target} = this.entity
          const time = this.lastMessage.time
          logger.event(`📢 subscribe/logs`, {id, target, time})
          api.socket.emit(`subscribe/logs`, {id, target, time})
          this.live = true
        }
      },
      unsubscribe(target) {
        target = target || this.entity.target
        if (this.live) {
          logger.event(`📢 unsubscribe/logs`, {target})
          api.socket.emit(`unsubscribe/logs`, {target})
          this.live = false
        }
      },
      pushMessage(msg) {
        this.pushMessages(msg)
        if (this.fromBottom < 5) {
          this.$nextTick(this.scrollToEnd)
        }
      },
      pushMessages(msgs) {
        const $lastMsg = this.$el.querySelector(`#msg-${this.lastMessage.id}`)
        const oldOffset = $lastMsg ? this.$refs.list.scrollTop - $lastMsg.offsetTop : 0
        const extraLogs = this.logs.concat(msgs)
        this.logs = extraLogs.splice(-this.count)
        if (extraLogs.length > 0) {
          this.isBeginning = false
        }
        if (msgs.length < this.filter.limit) {
          this.isEnd = true
        }
        this.$nextTick(() => {
          if (this.$refs.list) {
            const newOffset = $lastMsg ? this.$refs.list.scrollTop - $lastMsg.offsetTop : 0
            this.$refs.list.scrollBy(0, oldOffset - newOffset)
          }
        })
      },
      unshiftMessages(msgs) {
        const $firstMsg = this.$el.querySelector(`#msg-${this.firstMessage.id}`)
        const oldOffset = $firstMsg ? $firstMsg.offsetTop : 0
        const extraLogs = msgs.concat(this.logs)
        this.logs = extraLogs.splice(0, this.count)
        if (extraLogs.length > 0) {
          this.isEnd = false
        }
        if (msgs.length < this.filter.limit) {
          this.isBeginning = true
        }
        this.$nextTick(() => {
          if (this.$refs.list) {
            const newOffset = $firstMsg ? $firstMsg.offsetTop : 0
            this.$refs.list.scrollBy(0, newOffset - oldOffset)
          }
        })
      },
      onScroll: debounce(function() {
        if (this.$refs.list) {
          this.fromBottom = this.$refs.list.scrollHeight - this.$refs.list.scrollTop - this.$refs.list.offsetHeight
          this.fromTop = this.$refs.list.scrollTop
          if (this.lazyLoad && !this.lazyLoading) {
            if (this.fromTop < this.lazyLoadOffset && !this.isBeginning) {
              this.loadPrevPage()
            }
            if (this.fromBottom < this.lazyLoadOffset && !this.isEnd) {
              this.loadNextPage()
            }
          }
        }
      }, 100),
      scrollToStart() {
        utils.scrollTo(this.$refs.list, 0)
      },
      scrollToEnd() {
        utils.scrollTo(this.$refs.list, this.$refs.list.scrollHeight - this.$refs.list.offsetHeight)
      },
      search(filter = {}) {
        this.lazyLoading = true
        return this.entity.fetchLogs({...this.filter, ...filter}).then(messages => {
          return messages.data.map(formatMessage)
        }).catch(() => {
          return []
        }).finally(() => {
          this.lazyLoading = false
        })
      },
      loadPrevPage() {
        utils.reachGoal('LOGS_SEARCH_PAGE')
        const filter = {
          direction: -1,
          include: false,
          time: this.firstMessage.time,
        }
        logger.verbose('📃 Loading previous logs', {filter});
        this.search(filter).then(this.unshiftMessages)
      },
      loadNextPage() {
        utils.reachGoal('LOGS_SEARCH_PAGE')
        const filter = {
          direction: 1,
          include: false,
          time: this.lastMessage.time,
        }
        logger.verbose('📃 Loading next logs');
        this.search(filter).then(this.pushMessages)
      },
      searchMessage(message) {
        if (!this.hasSearch) {
          return false
        }
        const filter = {
          direction: 1,
          include: true,
          limit: this.count,
          time: message.time,
        }
        utils.reachGoal('LOGS_SEARCH_DATE')
        logger.verbose('📄 Loading logs by time', {filter});
        this.filter.text = ''
        this.isFilterVisible = false
        this.search(filter).then(newLogs => {
          this.logs = newLogs
          this.isBeginning = false
          this.isEnd = newLogs.length < filter.limit
          this.logs[0].highlighted = true
          this.$nextTick(() => {
            if (this.$refs.list.scrollTop) {
              this.$refs.list.scrollTop = 0
            } else {
              this.onScroll()
            }
          })
        })
      },
      searchText: debounce(function(text) {
        utils.reachGoal('LOGS_SEARCH_TEXT')
        const filter = {
          direction: -1,
          include: true,
          limit: this.count,
        }
        this.filter.text = text
        logger.verbose('📄 Loading logs by text', {filter});
        this.search(filter).then(newLogs => {
          this.logs = newLogs
          this.isBeginning = newLogs.length < filter.limit
          this.isEnd = true
          this.$nextTick(() => {
            this.$refs.list.scrollTop = this.$refs.list.scrollHeight
          })
        })
      }, 500),
      showFilter() {
        this.isFilterVisible = true
        this.$nextTick(() => {
          this.$refs.text.$el.querySelector('input').focus()
        })
      },
      hideFilter() {
        if (!this.filter.text) {
          this.isFilterVisible = false;
        }
      },
      openLogProviderSelector() {
        const service = this.entity._service._clone
        service.upsert = service.updateLogProvider
        this.$modals.updateService.open(service, 'updatingLogProvider')
      },
    },
    created() {
      this.init()
    },
    beforeDestroy() {
      this.unsubscribe()
      this.logs.length = 0
    },
  }

  function formatMessage(message) {
    const day = message.time.substr(0, 10)
    return {
      day,
      dayTitle: moment(day).format('LL'),
      highlighted: false,
      host: message.host_name,
      id: message.time.replace(/[.:-]/g, ''),
      info: message.info,
      isError: message.is_error,
      level: message.level,
      message: message.message,
      stderr: message.stderr,
      stdout: message.stdout,
      time: message.time,
      timeTitle: moment(message.time).format('HH:mm:ss'),
      // timestamp: message['@timestamp'],
      // message.container_hostname
      // message.service_name,
      // message.host
    }
  }
</script>

<style scoped>
  .log {
    position: relative;
    padding: 0!important;
    font-size: .9em;
  }
  .list {
    display: flex;
    flex-direction: column;
    height: 600px;
    overflow-y: scroll;
  }
  .segment .list {
    border: 1px solid #ddd;
  }
  .status {
    flex-shrink: 0;
    padding: 1em;
    border: none!important;
    box-shadow: none!important;
  }
  .no-logs {
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
  }
  .day {
    /* margin-bottom: 1em; */
  }
  .date {
    position: sticky;
    top: -1px;
    z-index: 1;
    padding: .7em;
    background-color: #fafafa;
    border-bottom: 1px solid #eee;
    text-align: center;
    font-weight: bold;
  }
  .filter {
    position: absolute!important;
    top: .5em;
    left: .5em;
    z-index: 5;
  }
  .filter-button {
    position: absolute;
    left: .5em;
    top: .5em;
    z-index: 5;
  }
  .log-provider {
    display: flex!important;
    justify-content: center;
    align-items: center;
    margin: -1px 0 0;
  }
  .log-provider .label {
    margin-left: .5em!important;
  }
  .log >>> .host {
    display: none;
  }
  .service.multicontainer.log >>> .host {
    display: block;
  }
</style>
